import { useEffect } from "react";
import { ChatConfig } from "src/reusable/Chat/types";
import { useChatStore } from "../stores/ChatStore";
import useResolvePersona from "./useResolvePersona";
import { useParams } from "react-router-dom";
import { AtomAssistChatPageParams } from "../AtomAssistChatPage";

export const useSyncPersonaToStore = ({
  initialConfig,
  initialUserMessage,
  allowUrlSync,
  ignoreDialogueHistory,
  dialogueId: dialogueIdProp,
}: {
  initialConfig?: ChatConfig;
  initialUserMessage?: string;
  allowUrlSync: boolean;
  ignoreDialogueHistory?: boolean;
  dialogueId?: number;
}) => {
  const { setPersona, setConfig, resetToBaseConfig, workflowPersona } =
    useChatStore();
  const { dialogueId: dialogueIdParam } = useParams<AtomAssistChatPageParams>();
  const resolvedDialogueId =
    dialogueIdProp !== undefined ? dialogueIdProp : Number(dialogueIdParam);
  const hasDialogueId =
    Number.isFinite(resolvedDialogueId) && resolvedDialogueId > 0;

  const {
    resolvedPersona,
    isLoading: isResolvingPersona,
    isError,
    ...rest
  } = useResolvePersona({
    initialConfig,
    initialUserMessage,
    allowUrlSync,
    ignoreDialogueHistory,
  });

  useEffect(() => {
    // Skip configuration updates if we're loading a historical dialogue
    // The dialogue-config API response will be the authoritative source
    if (hasDialogueId && !initialUserMessage) {
      return;
    }

    if (!isResolvingPersona) {
      // Priority 1: If we have initialConfig (from splash screen navigation), use it temporarily
      // Note: dialogue-config will override this if we're loading a historical dialogue
      if (initialConfig) {
        setConfig(initialConfig);

        // Set persona if we resolved one
        if (resolvedPersona) {
          setPersona(resolvedPersona);
        }
      }
      // Priority 2: Only apply resolved persona if we're NOT loading a dialogue
      else if (resolvedPersona && !hasDialogueId) {
        setPersona(resolvedPersona);
        setConfig(resolvedPersona.config);
      }
      // Priority 3: Use workflow persona from sidebar click (has config ID for explicit selection)
      else if (workflowPersona?.id) {
        setPersona({
          id: workflowPersona.id,
          slug: workflowPersona.slug,
          name: workflowPersona.name,
          description: workflowPersona.name,
          config: {
            chatConfigId: workflowPersona.id,
            modelId: null,
            modelVersionNum: null,
            datasets: [],
            chatControlMetadata: {},
            topSearchResults: 5,
            toolCalling: true,
          },
        });
        setConfig({
          chatConfigId: workflowPersona.id,
          modelId: null,
          modelVersionNum: null,
          datasets: [],
          chatControlMetadata: {},
          topSearchResults: 5,
          toolCalling: true,
        });
      }
      // Priority 4: Reset to base config - backend will use GeneralLLM from resource slots
      else {
        resetToBaseConfig();
      }
    }
  }, [
    resolvedPersona,
    isResolvingPersona,
    initialConfig,
    initialUserMessage,
    hasDialogueId,
    setPersona,
    setConfig,
    resetToBaseConfig,
    workflowPersona,
    resolvedDialogueId,
  ]);

  return {
    resolvedPersona,
    isLoading: isResolvingPersona,
    isError,
    ...rest,
  };
};
